import {
  CreatePointRulePayload,
  MAX_POINT_RULE_VALUE,
  MIN_POINT_RULE_VALUE,
  POINT_CUSTOM_WINDOW_MAX_MONTHS,
  POINT_RULE_ACTION_LABELS,
  POINT_RULE_KIND_LABELS,
  PointRule,
  PointRuleActionType,
  PointRuleChanges,
  PointRuleEditableField,
  PointRuleKind,
  PointWindowPeriod,
  canEditPointRuleField,
  getPointRuleDisplayName,
  isPointRuleActionRetired,
  isPointRuleActionWithoutAccrual,
} from "../../../models/pointRule";
import { EventSubType, EventType } from "../../../models/events";
import { toReportDateParam } from "../../../utils/constants/report-date-range";

/**
 * Estado del formulario de reglas de Chispas y su lógica pura: valores iniciales,
 * validación, cambios a enviar al editar y payload del alta. Sin render, así se
 * testea directo. Los números vacíos son null mientras el usuario escribe.
 */
export interface PointRuleFormValues {
  actionType: PointRuleActionType | null;
  ruleKind: PointRuleKind | null;
  baseRuleId: number | null;
  pointsValue: number | null;
  active: boolean;
  visibleToUser: boolean;
  displayTitle: string;
  displayDescription: string;
  iconKey: string | null;
  windowPeriod: PointWindowPeriod | null;
  /** Solo con windowPeriod "custom": día calendario elegido en el Calendar (hora local). */
  windowStartsOn: Date | null;
  windowEndsOn: Date | null;
  thresholdCount: number | null;
  streakPeriods: number | null;
  freeEventsOnly: boolean;
  eventTypeId: number | null;
  eventSubTypeId: number | null;
}

export type PointRuleFormErrorKey =
  | "actionType"
  | "ruleKind"
  | "baseRuleId"
  | "pointsValue"
  | "displayTitle"
  | "windowPeriod"
  | "windowStartsOn"
  | "windowEndsOn"
  | "thresholdCount"
  | "streakPeriods"
  | "eventTypeId";

export type PointRuleFormErrors = Partial<Record<PointRuleFormErrorKey, string>>;

export interface PointRuleFormScope {
  /** true en alta: no hay regla todavía y la acción, el tipo y la base se eligen. */
  creating: boolean;
  /** Campos que el usuario puede editar. En alta, todos. */
  canEdit: (field: PointRuleEditableField) => boolean;
  /** Reglas del tenant, para avisar de un duplicado antes de que lo rechace la base. */
  existingRules?: PointRule[];
  /** En edición, la regla abierta: no choca consigo misma. */
  currentRuleId?: number;
  /** En edición, si la regla ya estaba prendida: una acción sin motor se puede dejar prendida o apagar. */
  originalActive?: boolean;
  /** En edición, la fecha hasta guardada: un período que ya terminó se puede seguir editando. */
  originalWindowEndsOn?: Date | null;
  /** En edición, la fecha desde guardada: un período en curso se puede seguir editando. */
  originalWindowStartsOn?: Date | null;
}

/** Qué campos aplican según el tipo de regla. Un campo que no aplica no viaja. */
export interface PointRuleFieldApplicability {
  /** false solo en entity_amount: los puntos vienen de cada evento. */
  pointsValue: boolean;
  /** Solo entity_amount (Bonus por evento): tope opcional de puntos extra por evento. */
  bonusCap: boolean;
  /** windowPeriod y thresholdCount. */
  threshold: boolean;
  /** Desde y hasta: umbral con período "A medida". */
  customWindow: boolean;
  /** streakPeriods (y la regla base, que solo se elige en alta). */
  streak: boolean;
  /**
   * eventTypeId y eventSubTypeId: umbral de asistencia e inasistencia, donde el motor los usa.
   * Una sugerencia no tiene evento (point_rule_event_scope_check).
   */
  eventScope: boolean;
  /** Asistencia (atómica y por período), bonus por evento e inasistencia. */
  freeEventsOnly: boolean;
}

/** Contrato del backend: threshold_count 1..365, streak_periods 1..52. */
export const THRESHOLD_COUNT_RANGE = { min: 1, max: 365 } as const;
export const STREAK_PERIODS_RANGE = { min: 1, max: 52 } as const;

/**
 * Tipos de regla que admite cada acción en el alta, igual que el validador del backend
 * (getAllowedPointRuleKinds de worker-api). "Racha" es solo de Hito de racha y "Según el
 * evento" solo de Bonus por evento. "Umbral por período" es solo de Asistencia: la base también
 * lo permite para Sugerencia enviada, pero el backend todavía rechaza ese alta.
 */
export const getAllowedPointRuleKinds = (
  actionType: PointRuleActionType | null
): PointRuleKind[] => {
  if (actionType === null) return ["atomic", "threshold_window", "entity_amount", "streak"];
  if (actionType === "streak") return ["streak"];
  if (actionType === "event_bonus") return ["entity_amount"];
  if (actionType === "checkin") return ["atomic", "threshold_window"];
  return ["atomic"];
};

const POINT_RULE_ACTION_TYPES = (
  Object.keys(POINT_RULE_ACTION_LABELS) as PointRuleActionType[]
).filter((actionType) => !isPointRuleActionRetired(actionType));

/** Acciones que admiten un tipo de regla: la misma tabla que getAllowedPointRuleKinds, al revés. */
export const getAllowedPointRuleActions = (ruleKind: PointRuleKind | null): PointRuleActionType[] =>
  ruleKind === null
    ? POINT_RULE_ACTION_TYPES
    : POINT_RULE_ACTION_TYPES.filter((actionType) =>
        getAllowedPointRuleKinds(actionType).includes(ruleKind)
      );

/** "2026-10-01" → Date local del 1/10 (no UTC: en AR, UTC corre el día). */
export const dayKeyToLocalDate = (dayKey: string | null): Date | null => {
  if (dayKey === null) return null;
  const [year, month, day] = dayKey.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

/**
 * Último día que admite un período a medida que arranca en `startsOn`. Si el día de inicio no
 * existe en el mes destino, recorta al último día de ese mes (31/08 + 6 meses = 28/02), igual
 * que el backend. `addCalendarMonths` de Reporting desborda (daría 03/03), por eso no se usa.
 */
export const getCustomWindowMaxEndDate = (startsOn: Date): Date => {
  const targetMonth = startsOn.getMonth() + POINT_CUSTOM_WINDOW_MAX_MONTHS;
  const lastDayOfTargetMonth = new Date(startsOn.getFullYear(), targetMonth + 1, 0).getDate();
  return new Date(
    startsOn.getFullYear(),
    targetMonth,
    Math.min(startsOn.getDate(), lastDayOfTargetMonth)
  );
};

const toDayKeyOrNull = (date: Date | null): string | null =>
  date === null ? null : toReportDateParam(date);

/** Fechas del período a medida tal como viajan al backend; fuera de "custom" van null. */
export const resolveCustomWindowDayKeys = (
  values: PointRuleFormValues
): { windowStartsOn: string | null; windowEndsOn: string | null } =>
  values.ruleKind === "threshold_window" && values.windowPeriod === "custom"
    ? {
        windowStartsOn: toDayKeyOrNull(values.windowStartsOn),
        windowEndsOn: toDayKeyOrNull(values.windowEndsOn),
      }
    : { windowStartsOn: null, windowEndsOn: null };

type PointRuleEventScope = Pick<PointRule, "eventTypeId" | "eventSubTypeId">;

/** "Todos" (null) abarca cualquier tipo o subtipo, igual que en el backend. */
const eventScopesOverlap = (first: PointRuleEventScope, second: PointRuleEventScope): boolean => {
  if (first.eventTypeId === null || second.eventTypeId === null) return true;
  if (first.eventTypeId !== second.eventTypeId) return false;
  if (first.eventSubTypeId === null || second.eventSubTypeId === null) return true;
  return first.eventSubTypeId === second.eventSubTypeId;
};

/**
 * Hay una sola regla de umbral por acción y tipo/subtipo de evento, sea cual sea el período, la
 * cantidad o las fechas. En edición se compara solo si cambia el tipo o el subtipo: hay reglas
 * semanales sembradas que ya se superponen y tienen que poder seguir editándose.
 */
export const isThresholdScopeDuplicate = (
  duplicate: PointRule,
  values: PointRuleFormValues,
  currentRule?: PointRule
): boolean =>
  values.ruleKind === "threshold_window" &&
  duplicate.ruleKind === "threshold_window" &&
  values.windowPeriod === "custom" &&
  duplicate.windowPeriod === "custom" &&
  (currentRule === undefined ||
    currentRule.windowPeriod !== "custom" ||
    currentRule.eventTypeId !== values.eventTypeId ||
    currentRule.eventSubTypeId !== values.eventSubTypeId) &&
  eventScopesOverlap(duplicate, values);

/**
 * Regla del tenant que choca con el alta o la edición, igual que el backend: misma acción con la
 * misma cantidad (uq_point_rule_tenant_action_counters; el período y las fechas no cuentan), o
 * dos reglas de umbral de la misma acción para el mismo tipo y subtipo de evento.
 */
export const findDuplicatePointRule = (
  values: PointRuleFormValues,
  existingRules: PointRule[],
  currentRuleId?: number
): PointRule | null => {
  if (values.actionType === null || values.ruleKind === null) return null;
  const thresholdCount = values.ruleKind === "threshold_window" ? values.thresholdCount : 0;
  const streakPeriods = values.ruleKind === "streak" ? values.streakPeriods : 0;
  const currentRule = existingRules.find((rule) => rule.id === currentRuleId);
  return (
    existingRules.find(
      (rule) =>
        rule.id !== currentRuleId &&
        rule.actionType === values.actionType &&
        ((thresholdCount !== null &&
          streakPeriods !== null &&
          rule.thresholdCount === thresholdCount &&
          rule.streakPeriods === streakPeriods) ||
          isThresholdScopeDuplicate(rule, values, currentRule))
    ) ?? null
  );
};

export const DUPLICATE_POINT_RULE_ERROR = "Ya existe una regla igual";

/** Explica el choque: cuál regla, por qué no puede haber dos y qué hacer. */
export const describePointRuleDuplicate = (
  duplicate: PointRule,
  values: PointRuleFormValues,
  currentRule?: PointRule
): string => {
  const name = `"${getPointRuleDisplayName(duplicate)}"`;
  const action = POINT_RULE_ACTION_LABELS[duplicate.actionType];
  if (isThresholdScopeDuplicate(duplicate, values, currentRule)) {
    return `Ya existe la regla ${name} de ${action}: una campaña entre dos fechas para el mismo tipo y subtipo de evento. Solo puede haber una campaña entre fechas por tipo y subtipo, y una para todos los eventos los incluye a todos: elegí otro tipo o subtipo de evento, o editá esa regla.`;
  }
  if (values.ruleKind === "threshold_window") {
    const unit = duplicate.thresholdCount === 1 ? "actividad" : "actividades";
    return `Ya existe la regla ${name} de ${action} con ${duplicate.thresholdCount} ${unit}. Por ahora no puede haber dos reglas de la misma acción con la misma cantidad, aunque cambien el período o las fechas: elegí otra cantidad o editá esa regla.`;
  }
  if (values.ruleKind === "streak") {
    return `Ya existe la regla ${name}, que paga con el mismo número (${duplicate.streakPeriods}). Elegí otro número o editá esa regla.`;
  }
  const kind = values.ruleKind ? POINT_RULE_KIND_LABELS[values.ruleKind] : "";
  return `Ya existe la regla ${name} de ${action} (${kind}). Cada acción tiene una sola regla de este tipo: editá esa en lugar de crear otra.`;
};

export const normalizeText = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
};

const isIntegerInRange = (value: number | null, min: number, max: number): boolean =>
  value !== null && Number.isInteger(value) && value >= min && value <= max;

export const getPointRuleFieldApplicability = (
  ruleKind: PointRuleKind | null,
  actionType: PointRuleActionType | null,
  windowPeriod: PointWindowPeriod | null = null
): PointRuleFieldApplicability => {
  const threshold = ruleKind === "threshold_window";
  return {
    pointsValue: ruleKind !== "entity_amount",
    bonusCap: ruleKind === "entity_amount",
    threshold,
    customWindow: threshold && windowPeriod === "custom",
    streak: ruleKind === "streak",
    eventScope:
      (threshold && actionType === "checkin") || (ruleKind === "atomic" && actionType === "no_show"),
    freeEventsOnly:
      (threshold && actionType === "checkin") ||
      (ruleKind === "atomic" && (actionType === "no_show" || actionType === "checkin")) ||
      actionType === "event_bonus",
  };
};

const UNKNOWN_EVENT_SCOPE_NAME = "—";

/** Nombres del tipo y subtipo de evento de una regla: sin filtro es "Todos"; un id que el catálogo ya no trae, "—". */
export const resolvePointRuleEventScopeNames = (
  rule: Pick<PointRule, "eventTypeId" | "eventSubTypeId">,
  eventTypes: EventType[] | undefined,
  eventSubTypes: EventSubType[] | undefined
): { eventTypeName: string; eventSubTypeName: string } => ({
  eventTypeName:
    rule.eventTypeId == null
      ? "Todos"
      : eventTypes?.find((type) => type.eventTypeId === rule.eventTypeId)?.eventTypeName ??
        UNKNOWN_EVENT_SCOPE_NAME,
  eventSubTypeName:
    rule.eventSubTypeId == null
      ? "Todos"
      : eventSubTypes?.find((subType) => subType.eventSubTypeId === rule.eventSubTypeId)
          ?.eventSubTypeName ?? UNKNOWN_EVENT_SCOPE_NAME,
});

/**
 * La columna "Tipo de evento" del listado: "Deportivo – Fútbol", solo "Deportivo" si el subtipo es
 * Todos, "Todos" sin filtro, y "—" en las reglas que no se filtran por evento.
 */
export const formatPointRuleEventScope = (
  rule: PointRule,
  eventTypes: EventType[] | undefined,
  eventSubTypes: EventSubType[] | undefined
): string => {
  if (!getPointRuleFieldApplicability(rule.ruleKind, rule.actionType, rule.windowPeriod).eventScope) {
    return "—";
  }
  if (rule.eventTypeId == null) return "Todos";
  const { eventTypeName, eventSubTypeName } = resolvePointRuleEventScopeNames(
    rule,
    eventTypes,
    eventSubTypes
  );
  if (eventTypeName === UNKNOWN_EVENT_SCOPE_NAME) return UNKNOWN_EVENT_SCOPE_NAME;
  return rule.eventSubTypeId == null || eventSubTypeName === UNKNOWN_EVENT_SCOPE_NAME
    ? eventTypeName
    : `${eventTypeName} – ${eventSubTypeName}`;
};

export const buildInitialFormValues = (rule: PointRule | null): PointRuleFormValues => {
  if (rule === null) {
    return {
      actionType: null,
      ruleKind: null,
      baseRuleId: null,
      pointsValue: null,
      active: false,
      visibleToUser: false,
      displayTitle: "",
      displayDescription: "",
      iconKey: null,
      // Los umbrales nuevos se cuentan siempre entre dos fechas: semana y mes ya no se ofrecen.
      windowPeriod: "custom",
      windowStartsOn: null,
      windowEndsOn: null,
      thresholdCount: null,
      streakPeriods: null,
      freeEventsOnly: false,
      eventTypeId: null,
      eventSubTypeId: null,
    };
  }
  return {
    actionType: rule.actionType,
    ruleKind: rule.ruleKind,
    baseRuleId: rule.baseRuleId,
    pointsValue: rule.pointsValue,
    active: rule.active,
    visibleToUser: rule.visibleToUser,
    displayTitle: rule.displayTitle ?? "",
    displayDescription: rule.displayDescription ?? "",
    iconKey: rule.iconKey,
    windowPeriod: rule.windowPeriod,
    windowStartsOn: dayKeyToLocalDate(rule.windowStartsOn),
    windowEndsOn: dayKeyToLocalDate(rule.windowEndsOn),
    thresholdCount: rule.thresholdCount,
    streakPeriods: rule.streakPeriods,
    freeEventsOnly: rule.freeEventsOnly,
    eventTypeId: rule.eventTypeId,
    eventSubTypeId: rule.eventSubTypeId,
  };
};

export const POINT_RULE_WITHOUT_ACCRUAL_MESSAGE =
  "Esta acción todavía no otorga puntos: se puede dejar configurada, pero no activar.";

/** El Calendar entrega días a medianoche local: se compara contra el inicio de hoy. */
const isBeforeToday = (day: Date): boolean => {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return day.getTime() < startOfToday.getTime();
};

export const PAST_CUSTOM_WINDOW_ACTIVATION_MESSAGE =
  "Para volver a activar la regla, el período tiene que empezar hoy o después";

/** Período a medida que ya empezó: una regla apagada no se reactiva sin mover las fechas. */
export const hasPastCustomWindowStart = (values: PointRuleFormValues): boolean =>
  values.ruleKind === "threshold_window" &&
  values.windowPeriod === "custom" &&
  values.windowStartsOn !== null &&
  isBeforeToday(values.windowStartsOn);

/** Margen para que la fecha elegida no quede en el pasado si se guarda cerca de la medianoche. */
const REACTIVATION_START_MARGIN_MS = 5 * 60 * 1000;

/**
 * Apagar una regla visible la oculta también: así la guarda la base, y el formulario lo manda
 * explícito. Prenderla no cambia la visibilidad. Un hito de racha no toca la suya: el backend la
 * rechaza (streak_texts_locked_for_milestone).
 */
const hidesWhenDeactivated = (ruleKind: PointRuleKind | null): boolean => ruleKind !== "streak";

/** Activa sin mover fechas, con el mismo efecto sobre la visibilidad que withActiveToggled. */
export const withActiveValue = (
  values: PointRuleFormValues,
  active: boolean
): PointRuleFormValues =>
  !active && hidesWhenDeactivated(values.ruleKind)
    ? { ...values, active, visibleToUser: false }
    : { ...values, active };

/**
 * Al prender una regla apagada cuyo período a medida ya empezó, "Desde" pasa al día de dentro
 * de 5 minutos (hoy, salvo pasadas las 23:55) y "Hasta" queda vacío hasta que se elija.
 */
export const withActiveToggled = (
  values: PointRuleFormValues,
  active: boolean
): PointRuleFormValues => {
  const toggled = withActiveValue(values, active);
  if (!active || values.active || !hasPastCustomWindowStart(values)) return toggled;
  const windowStartsOn = new Date(Date.now() + REACTIVATION_START_MARGIN_MS);
  windowStartsOn.setHours(0, 0, 0, 0);
  return { ...toggled, windowStartsOn, windowEndsOn: null };
};

/** Regla apagada cuyo período a medida ya empezó: para prenderla hay que elegir fechas nuevas. */
export const needsNewCustomWindowToActivate = (rule: PointRule): boolean =>
  !rule.active && hasPastCustomWindowStart(buildInitialFormValues(rule));

/** Una fecha pasada solo se acepta si ya estaba guardada: en alta, o si se cambia, va de hoy en adelante. */
const isNewDayBeforeToday = (
  day: Date,
  original: Date | null | undefined,
  creating: boolean
): boolean =>
  isBeforeToday(day) && (creating || original == null || original.getTime() !== day.getTime());

/**
 * La regla "por acción" o "según el evento" es una sola por acción y tenant (la base no deja
 * repetirla), y los tenants nacen con todas sembradas: elegirla de nuevo siempre choca.
 * El umbral nuevo es siempre una campaña entre fechas, y hay una sola por tipo y subtipo: si ya
 * hay una para todos los eventos, cualquier campaña nueva de esa acción se le superpone.
 */
export const isPointRuleKindTaken = (
  actionType: PointRuleActionType,
  ruleKind: PointRuleKind,
  rules: PointRule[]
): boolean => {
  if (ruleKind === "threshold_window") {
    return rules.some(
      (rule) =>
        rule.actionType === actionType &&
        rule.ruleKind === "threshold_window" &&
        rule.windowPeriod === "custom" &&
        rule.eventTypeId === null
    );
  }
  return (
    (ruleKind === "atomic" || ruleKind === "entity_amount") &&
    rules.some((rule) => rule.actionType === actionType && rule.ruleKind === ruleKind)
  );
};

export const canCreatePointRuleForAction = (
  actionType: PointRuleActionType,
  rules: PointRule[]
): boolean =>
  getAllowedPointRuleKinds(actionType).some(
    (ruleKind) => !isPointRuleKindTaken(actionType, ruleKind, rules)
  );

/**
 * Errores de validación en cliente. Cada campo se valida solo si su control se
 * muestra (canEdit): un error en un campo de solo lectura no lo puede corregir nadie.
 */
export const validatePointRuleForm = (
  values: PointRuleFormValues,
  scope: PointRuleFormScope
): PointRuleFormErrors => {
  const applies = getPointRuleFieldApplicability(
    values.ruleKind,
    values.actionType,
    values.windowPeriod
  );
  const errors: PointRuleFormErrors = {};

  if (scope.creating) {
    if (values.actionType === null) errors.actionType = "Elegí la acción";
    if (values.ruleKind === null) errors.ruleKind = "Elegí el tipo de regla";
    if (
      values.ruleKind !== null &&
      !getAllowedPointRuleKinds(values.actionType).includes(values.ruleKind)
    ) {
      errors.ruleKind = "Ese tipo de regla no aplica a esta acción";
    }
  }

  if (
    applies.bonusCap &&
    scope.canEdit("pointsValue") &&
    values.pointsValue !== null &&
    !isIntegerInRange(values.pointsValue, MIN_POINT_RULE_VALUE, MAX_POINT_RULE_VALUE)
  ) {
    errors.pointsValue = `Ingresá un número entero entre ${MIN_POINT_RULE_VALUE} y ${MAX_POINT_RULE_VALUE}, o dejalo vacío para no poner tope`;
  }

  if (
    values.active &&
    values.actionType !== null &&
    isPointRuleActionWithoutAccrual(values.actionType) &&
    scope.originalActive !== true
  ) {
    errors.actionType = POINT_RULE_WITHOUT_ACCRUAL_MESSAGE;
  }

  if (applies.pointsValue && scope.canEdit("pointsValue")) {
    if (values.pointsValue !== null) {
      if (!isIntegerInRange(values.pointsValue, MIN_POINT_RULE_VALUE, MAX_POINT_RULE_VALUE)) {
        errors.pointsValue = `Ingresá un número entero entre ${MIN_POINT_RULE_VALUE} y ${MAX_POINT_RULE_VALUE}`;
      }
    } else if (values.active && !scope.creating) {
      errors.pointsValue = "Cargá los puntos antes de activar la regla";
    } else {
      errors.pointsValue = "Cargá los puntos de la regla";
    }
  }

  if (applies.threshold) {
    if (scope.canEdit("windowPeriod") && values.windowPeriod === null) {
      errors.windowPeriod = "Elegí el período";
    }
    if (applies.customWindow && scope.canEdit("windowStartsOn") && scope.canEdit("windowEndsOn")) {
      if (values.windowStartsOn === null) {
        errors.windowStartsOn = "Elegí desde cuándo cuenta";
      } else if (
        isNewDayBeforeToday(values.windowStartsOn, scope.originalWindowStartsOn, scope.creating)
      ) {
        errors.windowStartsOn = "La fecha desde no puede ser anterior a hoy";
      } else if (values.active && scope.originalActive !== true && hasPastCustomWindowStart(values)) {
        errors.windowStartsOn = PAST_CUSTOM_WINDOW_ACTIVATION_MESSAGE;
      }
      if (values.windowEndsOn === null) {
        errors.windowEndsOn = "Elegí hasta cuándo cuenta";
      } else if (values.windowStartsOn !== null) {
        if (values.windowEndsOn.getTime() < values.windowStartsOn.getTime()) {
          errors.windowEndsOn = "Tiene que ser igual o posterior a la fecha desde";
        } else if (
          values.windowEndsOn.getTime() > getCustomWindowMaxEndDate(values.windowStartsOn).getTime()
        ) {
          errors.windowEndsOn = `El período puede durar hasta ${POINT_CUSTOM_WINDOW_MAX_MONTHS} meses`;
        } else if (
          isNewDayBeforeToday(values.windowEndsOn, scope.originalWindowEndsOn, scope.creating)
        ) {
          errors.windowEndsOn = "La fecha hasta ya pasó: un período que terminó no le paga a nadie";
        }
      }
    }
    if (
      scope.canEdit("thresholdCount") &&
      !isIntegerInRange(values.thresholdCount, THRESHOLD_COUNT_RANGE.min, THRESHOLD_COUNT_RANGE.max)
    ) {
      errors.thresholdCount = `Ingresá un número entero entre ${THRESHOLD_COUNT_RANGE.min} y ${THRESHOLD_COUNT_RANGE.max}`;
    }
  }

  if (
    applies.streak &&
    scope.canEdit("streakPeriods") &&
    !isIntegerInRange(values.streakPeriods, STREAK_PERIODS_RANGE.min, STREAK_PERIODS_RANGE.max)
  ) {
    errors.streakPeriods = `Ingresá un número entero entre ${STREAK_PERIODS_RANGE.min} y ${STREAK_PERIODS_RANGE.max}`;
  }

  if (
    applies.eventScope &&
    scope.canEdit("eventTypeId") &&
    values.eventSubTypeId !== null &&
    values.eventTypeId === null
  ) {
    errors.eventTypeId = "Elegí también el tipo de evento";
  }

  if (
    scope.canEdit("displayTitle") &&
    values.visibleToUser &&
    normalizeText(values.displayTitle) === null
  ) {
    errors.displayTitle = "Una regla visible necesita un título";
  }

  // El duplicado se marca en el campo que lo resuelve: la cantidad, los períodos o el tipo.
  // La explicación larga (describePointRuleDuplicate) la muestra el modal arriba del formulario.
  const duplicate = !errors.ruleKind
    ? findDuplicatePointRule(values, scope.existingRules ?? [], scope.currentRuleId)
    : null;
  if (duplicate !== null) {
    const currentRule = scope.existingRules?.find((rule) => rule.id === scope.currentRuleId);
    if (isThresholdScopeDuplicate(duplicate, values, currentRule)) {
      errors.eventTypeId = DUPLICATE_POINT_RULE_ERROR;
    } else if (values.ruleKind === "threshold_window") {
      errors.thresholdCount = DUPLICATE_POINT_RULE_ERROR;
    } else if (values.ruleKind === "streak") {
      errors.streakPeriods = DUPLICATE_POINT_RULE_ERROR;
    } else {
      errors.ruleKind = DUPLICATE_POINT_RULE_ERROR;
    }
  }

  return errors;
};

/**
 * Solo los campos editables cuyo valor cambió. Nunca un campo no editable. En la regla por
 * evento (entity_amount) `pointsValue` es el tope opcional de puntos extra por evento.
 */
export const buildPointRuleChanges = (
  rule: PointRule,
  values: PointRuleFormValues
): PointRuleChanges => {
  const applies = getPointRuleFieldApplicability(rule.ruleKind, rule.actionType, rule.windowPeriod);
  const canEdit = (field: PointRuleEditableField): boolean => canEditPointRuleField(rule, field);
  const changes: PointRuleChanges = {};

  if (
    (applies.pointsValue || applies.bonusCap) &&
    canEdit("pointsValue") &&
    values.pointsValue !== rule.pointsValue
  ) {
    changes.pointsValue = values.pointsValue;
  }
  if (canEdit("active") && values.active !== rule.active) {
    changes.active = values.active;
  }
  if (canEdit("visibleToUser") && values.visibleToUser !== rule.visibleToUser) {
    changes.visibleToUser = values.visibleToUser;
  }
  const displayTitle = normalizeText(values.displayTitle);
  if (canEdit("displayTitle") && displayTitle !== rule.displayTitle) {
    changes.displayTitle = displayTitle;
  }
  const displayDescription = normalizeText(values.displayDescription);
  if (canEdit("displayDescription") && displayDescription !== rule.displayDescription) {
    changes.displayDescription = displayDescription;
  }
  if (canEdit("iconKey") && values.iconKey !== rule.iconKey) {
    changes.iconKey = values.iconKey;
  }
  if (canEdit("windowPeriod") && values.windowPeriod !== rule.windowPeriod) {
    changes.windowPeriod = values.windowPeriod;
  }
  const customWindow = resolveCustomWindowDayKeys(values);
  if (canEdit("windowStartsOn") && customWindow.windowStartsOn !== rule.windowStartsOn) {
    changes.windowStartsOn = customWindow.windowStartsOn;
  }
  if (canEdit("windowEndsOn") && customWindow.windowEndsOn !== rule.windowEndsOn) {
    changes.windowEndsOn = customWindow.windowEndsOn;
  }
  if (
    canEdit("thresholdCount") &&
    values.thresholdCount !== null &&
    values.thresholdCount !== rule.thresholdCount
  ) {
    changes.thresholdCount = values.thresholdCount;
  }
  if (
    canEdit("streakPeriods") &&
    values.streakPeriods !== null &&
    values.streakPeriods !== rule.streakPeriods
  ) {
    changes.streakPeriods = values.streakPeriods;
  }
  if (canEdit("freeEventsOnly") && values.freeEventsOnly !== rule.freeEventsOnly) {
    changes.freeEventsOnly = values.freeEventsOnly;
  }
  if (canEdit("eventTypeId") && values.eventTypeId !== rule.eventTypeId) {
    changes.eventTypeId = values.eventTypeId;
  }
  if (canEdit("eventSubTypeId") && values.eventSubTypeId !== rule.eventSubTypeId) {
    changes.eventSubTypeId = values.eventSubTypeId;
  }
  return changes;
};

/**
 * Lo que viaja al PUT al prender o apagar desde el switch de la tabla. Apagar una regla visible la
 * oculta también, como withActiveToggled en el modal, pero solo si el rol puede editar la
 * visibilidad: un campo que no es editable no viaja (ver buildPointRuleChanges).
 */
export const buildActiveToggleChanges = (rule: PointRule): PointRuleChanges => {
  if (!rule.active) return { active: true };
  const hidesRule =
    rule.visibleToUser &&
    hidesWhenDeactivated(rule.ruleKind) &&
    canEditPointRuleField(rule, "visibleToUser");
  return hidesRule ? { active: false, visibleToUser: false } : { active: false };
};

/**
 * Payload del alta. Solo viajan los campos que aplican al tipo elegido y tienen
 * valor: el backend rechaza campos que no corresponden al rule_kind.
 */
export const buildCreatePointRulePayload = (
  values: PointRuleFormValues,
  tenantId: string
): CreatePointRulePayload | null => {
  if (values.actionType === null || values.ruleKind === null) return null;

  const applies = getPointRuleFieldApplicability(
    values.ruleKind,
    values.actionType,
    values.windowPeriod
  );
  const payload: CreatePointRulePayload = {
    tenantId,
    actionType: values.actionType,
    ruleKind: values.ruleKind,
    active: values.active,
    visibleToUser: values.visibleToUser,
  };

  const displayTitle = normalizeText(values.displayTitle);
  if (displayTitle !== null) payload.displayTitle = displayTitle;
  const displayDescription = normalizeText(values.displayDescription);
  if (displayDescription !== null) payload.displayDescription = displayDescription;
  if (values.iconKey !== null) payload.iconKey = values.iconKey;

  if ((applies.pointsValue || applies.bonusCap) && values.pointsValue !== null) {
    payload.pointsValue = values.pointsValue;
  }
  if (applies.threshold) {
    if (values.windowPeriod !== null) payload.windowPeriod = values.windowPeriod;
    if (values.thresholdCount !== null) payload.thresholdCount = values.thresholdCount;
  }
  if (applies.customWindow) {
    const customWindow = resolveCustomWindowDayKeys(values);
    if (customWindow.windowStartsOn !== null) payload.windowStartsOn = customWindow.windowStartsOn;
    if (customWindow.windowEndsOn !== null) payload.windowEndsOn = customWindow.windowEndsOn;
  }
  if (applies.streak && values.streakPeriods !== null) {
    payload.streakPeriods = values.streakPeriods;
  }
  if (values.ruleKind === "streak" && values.baseRuleId !== null) {
    payload.baseRuleId = values.baseRuleId;
  }
  if (applies.freeEventsOnly) payload.freeEventsOnly = values.freeEventsOnly;
  if (applies.eventScope) {
    if (values.eventTypeId !== null) payload.eventTypeId = values.eventTypeId;
    if (values.eventSubTypeId !== null) payload.eventSubTypeId = values.eventSubTypeId;
  }

  return payload;
};

/**
 * Regla armada desde el formulario en vivo, solo para formatear el valor del preview.
 * `formatPointRuleValue` lee ruleKind, pointsValue e isPenalty; el resto de los campos
 * de la regla no se usa acá.
 */
export const buildPointRulePreview = (
  values: PointRuleFormValues,
  tenantId: string,
  isPenalty: boolean
): PointRule => ({
  id: 0,
  tenantId,
  actionType: values.actionType ?? "checkin",
  ruleKind: values.ruleKind ?? "atomic",
  baseRuleId: values.baseRuleId,
  pointsValue: values.pointsValue,
  windowPeriod: values.windowPeriod,
  ...resolveCustomWindowDayKeys(values),
  thresholdCount: values.thresholdCount ?? 0,
  streakPeriods: values.streakPeriods ?? 0,
  freeEventsOnly: values.freeEventsOnly,
  eventTypeId: values.eventTypeId,
  eventSubTypeId: values.eventSubTypeId,
  displayTitle: normalizeText(values.displayTitle),
  displayDescription: normalizeText(values.displayDescription),
  iconKey: values.iconKey,
  visibleToUser: values.visibleToUser,
  active: values.active,
  effectiveFrom: "",
  updatedAt: "",
  isPenalty,
  hasAwardedPoints: false,
  editableFields: [],
  // El preview es una regla suelta del formulario en vivo: no tiene hermanas con las que apilarse.
  stackedWithRuleId: null,
});
